import CustomerCreationModal from '@/components/comptoir/CustomerCreationModal'
import { Customer, SharedProps } from '@/types'
import { formatName } from '@/utils/format'
import { Head, router, usePage } from '@inertiajs/react'
import { ActionIcon, Button, Group, Menu, Paper, Stack, Table, TextInput, Title } from '@mantine/core'
import { useDisclosure, useIntersection } from '@mantine/hooks'
import { IconDotsVertical, IconPencil, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'

const PAGE_SIZE = 20

export default function CustomersPage({ customers }: { customers: Customer[] }) {
  const { current_organization } = usePage<SharedProps>().props
  const organizationSlug = current_organization!.slug
  const [searchQuery, setSearchQuery] = useState('')
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE)
  const [creationOpened, creation] = useDisclosure(false)

  const handleCustomerCreated = () => {
    creation.close()
    router.reload({ only: ['customers'] })
  }

  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers

    const normalizedQuery = searchQuery.toLowerCase().trim()
    const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean)

    return customers.filter((customer) => {
      const firstName = customer.first_name?.toLowerCase() || ''
      const lastName = customer.last_name?.toLowerCase() || ''
      const phoneNumber = customer.phone_number?.replace(/\s/g, '').toLowerCase() || ''
      const email = customer.email?.toLowerCase() || ''

      const matchesAllTerms = queryTerms.every(
        (term) =>
          firstName.includes(term) || lastName.includes(term) || phoneNumber.includes(term) || email.includes(term)
      )

      return matchesAllTerms
    })
  }, [customers, searchQuery])

  // Reset infinite scroll when filters change so users see results from the top
  useEffect(() => setDisplayLimit(PAGE_SIZE), [searchQuery])

  const displayedCustomers = filteredCustomers.slice(0, displayLimit)
  const hasMore = filteredCustomers.length > displayLimit
  const handleShowMore = () => setDisplayLimit((prev) => prev + PAGE_SIZE)
  const { ref: loadMoreRef, entry } = useIntersection()

  useEffect(() => {
    if (entry?.isIntersecting && hasMore) handleShowMore()
  }, [entry?.isIntersecting, hasMore])

  const handleDelete = (customer: Customer) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le client ?`)) {
      router.delete(`/organizations/${current_organization?.slug}/customers/${customer.id}`)
    }
  }

  return (
    <>
      <Head title={`Clients - ${current_organization?.name}`} />

      <Stack>
        <Group justify="space-between" align="center">
          <Title>Clients</Title>
          <Button leftSection={<IconPlus size={18} />} onClick={creation.open}>
            Nouveau client
          </Button>
        </Group>

        <Group align="flex-end">
          <TextInput
            placeholder="Rechercher par prénom, nom, téléphone ou email..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
          />
        </Group>

        <Paper withBorder radius="md" p="md">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nom</Table.Th>
                <Table.Th>Téléphone</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {displayedCustomers.map((customer) => (
                <Table.Tr
                  key={customer.id}
                  onClick={() => router.get(`/organizations/${current_organization?.slug}/customers/${customer.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>
                    {formatName(customer)}
                  </Table.Td>
                  <Table.Td>{customer.phone_number}</Table.Td>
                  <Table.Td>{customer.email}</Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Menu shadow="md" width={200} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>

                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() =>
                            router.get(`/organizations/${current_organization?.slug}/customers/${customer.id}/edit`)
                          }
                        >
                          Modifier
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => handleDelete(customer)}
                        >
                          Supprimer
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        {hasMore && (
          <>
            <div ref={loadMoreRef} style={{ height: 1 }} />
            <Button variant="subtle" onClick={handleShowMore} fullWidth>
              Afficher plus
            </Button>
          </>
        )}
      </Stack>

      <CustomerCreationModal
        opened={creationOpened}
        onClose={creation.close}
        onCreated={handleCustomerCreated}
        organizationSlug={organizationSlug}
      />
    </>
  )
}
